const S1 = "Screenshot%202026-05-29%20at%20";
const S2 = "Screenshot%202026-07-05%20at%20";
const CDN = "https://img1.wsimg.com/isteam/ip/81aa8cb1-bad5-422b-adff-b833721955df";
const s1 = (t) => `${CDN}/${S1}${t}.png/:/rs=w:600,cg:true,m`;
const s2 = (t) => `${CDN}/${S2}${t}.png/:/rs=w:600,cg:true,m`;

const PAPER = {
  title:
    "Technology-Human Disequilibrium: A conceptual paper for human-centered technological systems",
  citation: "Puica, E. (2026), Mentheris",
  href: "https://ssrn.com/abstract=6426239",
};

const THD_FRAMEWORK = {
  name: ["Technology-Human", "Disequilibrium"],
  definition:
    "A systemic imbalance that emerges when technological development advances faster than the ability of human systems to understand, integrate, and effectively use it.",
  foundation:
    "This session draws on the theory-building conceptual paper Technology-Human Disequilibrium: A conceptual paper for human-centered technological systems (Puica, 2026, Mentheris).",
  paperHref: PAPER.href,
};

export const SESSIONS = [
  {
    slug: "session1",
    n: "01",
    status: "past",
    statusLabel: "Completed",
    series: "Technology-Human Disequilibrium",
    title: "The adaptation gap: when technology outpaces human capacity",
    headline: [
      "When technology moves faster",
      "than our capacity to adapt,",
      "what happens in between?",
    ],
    lede:
      "The opening session of the series introduced the concept of Technology-Human Disequilibrium and explored what drives this gap, what it looks like in practice, and why it matters for research, organisations and society.",
    meta: [
      ["Date", "28 May 2026 · 15:30 CET / 16:30 EET"],
      ["Duration", "60 minutes"],
      ["Format", "Online · Free · Open registration"],
      ["Facilitator", "Dr. Elena Puica · Mentheris"],
    ],
    context: [
      "We assume that when technology changes, people adapt. But adaptation is not automatic, not uniform, and not guaranteed.",
      "This session introduced the concept of Technology-Human Disequilibrium: a systemic imbalance that emerges when technological development advances faster than the ability of human systems to understand, integrate, and effectively use it. The session explored what drives this gap, what it looks like in practice, and why it matters for research, organizations, and society.",
    ],
    question:
      "When technology moves faster than our capacity to adapt, what happens in between?",
    examined: [
      {
        n: "01",
        title: "Technological expansion",
        text: "Technological development continues to advance, moving faster than the human systems expected to absorb it.",
      },
      {
        n: "02",
        title: "Human adaptive capacity",
        text: "Human systems need time to understand, integrate, and effectively use what changes. That capacity does not expand at the same pace.",
      },
      {
        n: "03",
        title: "The emerging imbalance",
        text: "When the two fall out of step, a systemic imbalance emerges: Technology-Human Disequilibrium.",
      },
      {
        n: "04",
        title: "Why it matters",
        text: "The gap is not abstract. It has practical consequences for research, organizations, and society.",
      },
    ],
    framework: THD_FRAMEWORK,
    ideas: [
      {
        statement: "Adaptation is not automatic.",
        explanation:
          "We assume that when technology changes, people adapt. But adaptation is not automatic, not uniform, and not guaranteed.",
      },
      {
        statement: "The gap is systemic, not individual.",
        explanation:
          "Disequilibrium emerges when technological development advances faster than the ability of human systems to understand, integrate, and effectively use it.",
      },
      {
        statement: "The consequences reach beyond technology.",
        explanation:
          "What begins as a distance between development and adaptation shapes research, organizations, and society.",
      },
    ],
    openQuestions: [
      "What drives the gap between technological development and human adaptive capacity?",
      "What does disequilibrium look like in practice?",
      "Why does it matter for research, organizations, and society?",
    ],
    materials: [
      {
        type: "slides",
        label: "View the presentation",
        note: "Session slides · 10 frames",
        slides: [
          s1("15.29.08"),
          s1("15.29.33"),
          s1("15.29.44"),
          s1("15.30.44"),
          s1("15.30.52"),
          s1("15.31.00"),
          s1("15.31.08"),
          s1("15.31.31"),
          s1("15.31.23"),
          s1("15.31.15"),
        ],
      },
      { type: "link", label: "Read the related research", note: PAPER.citation, href: PAPER.href },
    ],
    register: null,
    prev: null,
    next: { slug: "session2", n: "02" },
  },
  {
    slug: "session2",
    n: "02",
    status: "past",
    statusLabel: "Past session",
    series: "Technology-Human Disequilibrium",
    title: "The framework in focus: understanding Technology-Human Disequilibrium",
    headline: [
      "When technology and human capacity",
      "fall out of step,",
      "what does the gap look like?",
    ],
    lede:
      "The second session went deeper into the conceptual framework itself: its components, the dynamics between equilibrium and disequilibrium, and what the adaptive gap looks like across individual, organisational and societal levels.",
    meta: [
      ["Date", "25 June 2026 · 15:30 CET / 16:30 EET"],
      ["Duration", "60 minutes"],
      ["Format", "Online · Free"],
      ["Facilitator", "Dr. Elena Puica · Mentheris"],
    ],
    context: [
      "Session 1 introduced the concept of Technology-Human Disequilibrium and the conditions under which it emerges.",
      "Session 2 went deeper into the conceptual framework itself: its components, the dynamics between equilibrium and disequilibrium, and what the adaptive gap looks like across individual, organizational, and societal levels. The session examined the structural drivers of technological expansion, the limits of human adaptive capacity, and the multi-level consequences when the two fall out of step.",
    ],
    question:
      "When technology and human capacity fall out of step, what does the adaptive gap look like?",
    examined: [
      {
        n: "01",
        title: "Structural drivers of expansion",
        text: "The session examined the structural drivers of technological expansion: the forces that keep development moving faster than human systems can follow.",
      },
      {
        n: "02",
        title: "Limits of adaptive capacity",
        text: "Against that acceleration sit the limits of human adaptive capacity: the time human systems need to understand, integrate and use what changes.",
      },
      {
        n: "03",
        title: "Equilibrium and disequilibrium",
        text: "The framework describes the dynamics between equilibrium and disequilibrium: how balance is lost, and under what conditions it returns.",
      },
      {
        n: "04",
        title: "Consequences across levels",
        text: "The adaptive gap appears across individual, organizational, and societal levels, with consequences at each.",
      },
    ],
    framework: THD_FRAMEWORK,
    ideas: [
      {
        statement: "The framework has moving parts.",
        explanation:
          "Session 2 opened up the components of Technology-Human Disequilibrium and the dynamics between equilibrium and disequilibrium.",
      },
      {
        statement: "The gap is multi-level.",
        explanation:
          "The adaptive gap appears across individual, organizational, and societal levels, and looks different at each.",
      },
    ],
    openQuestions: null,
    materials: [
      {
        type: "slides",
        label: "View the presentation",
        note: "Session slides · 8 frames",
        slides: [
          s2("00.32.42"),
          s2("00.32.47"),
          s2("00.32.49"),
          s2("00.32.54"),
          s2("00.32.56"),
          `${CDN}/${S2}00.27.31.png/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:600,cg:true`,
          s2("00.33.05"),
          s2("00.33.07"),
        ],
      },
      { type: "link", label: "Read the related research", note: PAPER.citation, href: PAPER.href },
    ],
    register: null,
    prev: { slug: "session1", n: "01" },
    next: { slug: "session3", n: "03" },
  },
  {
    slug: "session3",
    n: "03",
    status: "upcoming",
    statusLabel: "Upcoming",
    series: "Technology-Human Disequilibrium",
    title: "Human-centered transformation",
    headline: [
      "When technology accelerates,",
      "why does meaningful collaboration",
      "become structurally difficult?",
    ],
    lede:
      "The third session explores how disequilibrium produces knowledge fragmentation, and why meaningful collaboration becomes structurally difficult in accelerating technological environments.",
    meta: [
      ["Date", "September 2026"],
      ["Format", "Online · Free · Open registration"],
    ],
    context: [
      "The third session of the series moves from the framework to one of its consequences: knowledge fragmentation.",
      "The session explores how disequilibrium produces knowledge fragmentation, and why meaningful collaboration becomes structurally difficult in accelerating technological environments.",
    ],
    question:
      "When technology accelerates, why does meaningful collaboration become structurally difficult?",
    examined: null,
    framework: null,
    ideas: null,
    openQuestions: null,
    materials: null,
    register: {
      label: "Register for this Dialogue",
      href: "https://mentheris.com/session3",
    },
    prev: { slug: "session2", n: "02" },
    next: { slug: "session4", n: "04" },
  },
  {
    slug: "session4",
    n: "04",
    status: "soon",
    statusLabel: "Coming soon",
    series: "Technology-Human Disequilibrium",
    title: "Rethinking collaboration: meaningful collaboration in digital systems",
    headline: [
      "What do infrastructural responses",
      "to disequilibrium look like?",
    ],
    lede:
      "The fourth session connects all three tensions and explores what infrastructural responses look like: for research, organisations and governance.",
    meta: [
      ["Date", "October 2026"],
      ["Format", "Online · Free"],
    ],
    context: [
      "The closing session of the series connects all three tensions examined across the earlier dialogues.",
      "The session explores what infrastructural responses look like: for research, organizations, and governance.",
    ],
    question: "What do infrastructural responses to disequilibrium look like?",
    examined: null,
    framework: null,
    ideas: null,
    openQuestions: null,
    materials: null,
    register: null,
    prev: { slug: "session3", n: "03" },
    next: null,
  },
];

export function getSession(slug) {
  return SESSIONS.find((s) => s.slug === slug) || null;
}
